import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { cn } from '@client/utils/styling/cn';
import * as Sentry from '@sentry/react';
import {
    createContext,
    lazy,
    type ReactNode,
    Suspense,
    useContext,
    useEffect,
    useState,
    useSyncExternalStore,
} from 'react';

type MarkdownRendererModule = typeof import('./MarkdownRenderer');

// Streamdown (with remark-gfm and Shiki) is large, and many detail routes import `Markdown`
// only to render it after a user action, so load the renderer on demand.
let rendererPromise: Promise<MarkdownRendererModule> | undefined;
let loadedRenderer: MarkdownRendererModule['MarkdownRenderer'] | undefined;
// Chunk-load rejections, so the error boundary can tell them apart from render errors.
const rendererLoadErrors = new WeakSet<object>();

function loadMarkdownRenderer(): Promise<MarkdownRendererModule> {
    rendererPromise ??= import('./MarkdownRenderer').then(
        (module) => {
            loadedRenderer = module.MarkdownRenderer;
            return module;
        },
        (error: unknown) => {
            if (typeof error === 'object' && error !== null) rendererLoadErrors.add(error);
            // Allow a later mount or preload to retry a failed chunk request.
            rendererPromise = undefined;
            throw error;
        },
    );
    return rendererPromise;
}

// React caches a rejected import on the lazy component itself, so each mount gets its own:
// a remount after a failed load then retries through the shared loader.
function createLazyMarkdownRenderer() {
    return lazy(() => loadMarkdownRenderer().then((module) => ({ default: module.MarkdownRenderer })));
}

/**
 * Loads the Markdown renderer chunk without rendering anything. Use it as the loader of a
 * route that renders markdown on load: the returned promise settles once the renderer is
 * ready, so the route's first paint shows formatted output rather than the plain-text
 * fallback followed by a reflow.
 */
export async function preloadMarkdownRenderer(): Promise<void> {
    // A failed preload is not an error: the component retries and reports on mount.
    await loadMarkdownRenderer().catch(() => undefined);
}

// Once the chunk fails, every mounted `Markdown` falls back at the same time; only the
// earliest-mounted of them shows the notice, and it passes to the next when that one unmounts.
let failureNoticeOwners: readonly symbol[] = [];
const failureNoticeListeners = new Set<() => void>();

function setFailureNoticeOwners(owners: readonly symbol[]) {
    failureNoticeOwners = owners;
    for (const listener of failureNoticeListeners) listener();
}

function subscribeFailureNoticeOwners(listener: () => void) {
    failureNoticeListeners.add(listener);
    return () => {
        failureNoticeListeners.delete(listener);
    };
}

function RendererLoadFailureNotice() {
    const [token] = useState(() => Symbol('markdown-failure-notice'));
    const owners = useSyncExternalStore(subscribeFailureNoticeOwners, () => failureNoticeOwners);

    useEffect(() => {
        setFailureNoticeOwners([...failureNoticeOwners, token]);
        return () => setFailureNoticeOwners(failureNoticeOwners.filter((owner) => owner !== token));
    }, [token]);

    if (owners[0] !== token) return null;

    // React caches a rejected lazy import on its component type, and the browser can cache the
    // failed module request, so a reload is the dependable way to get formatting back.
    return (
        <div role="status" className="mb-1 flex items-center gap-2 text-muted-foreground text-xs">
            Formatting could not be loaded.
            <Button type="link" size="micro" onClick={() => window.location.reload()}>
                Reload page
            </Button>
        </div>
    );
}

// The error boundary mounts a function fallback as a component, so it must be one stable
// type: a per-render function would remount the notice on every prop update, moving it to
// another instance and dropping keyboard focus. The current source reaches it via context.
const SourceFallbackContext = createContext<ReactNode>(null);

function RendererErrorFallback({ error }: { error: unknown }) {
    const fallback = useContext(SourceFallbackContext);
    // A reload can only help when the chunk failed to load, not when the renderer threw.
    const isLoadError = typeof error === 'object' && error !== null && rendererLoadErrors.has(error);
    return (
        <>
            {isLoadError && <RendererLoadFailureNotice />}
            {fallback}
        </>
    );
}

/**
 * Variant options for the Markdown component styling.
 * - `default`: Standard styling for general content
 * - `document`: Enhanced styling for longer documents with better heading hierarchy
 * - `chat`: Compact styling optimised for chat messages
 * - `compact`: Minimal styling with reduced spacing
 *
 * Styles are defined in `styling/markdown.css` using `data-streamdown` selectors.
 */
export type MarkdownVariant = 'default' | 'document' | 'chat' | 'compact';

/**
 * Rendering mode for the Markdown component.
 * - `streaming`: Optimised for AI-powered streaming with incomplete markdown handling
 * - `static`: Standard rendering for complete, static content
 */
export type MarkdownMode = 'streaming' | 'static';

/**
 * Caret style for streaming indicator.
 * - `block`: Block-style cursor (default for streaming)
 * - `circle`: Circle-style cursor
 * - `none`: No caret shown
 */
export type MarkdownCaret = 'block' | 'circle' | 'none';

export interface MarkdownProps {
    /** The markdown content to render */
    children: string;
    /** Styling variant - see styling/markdown.css for styles */
    variant?: MarkdownVariant;
    /** Rendering mode - streaming for AI output, static for complete content */
    mode?: MarkdownMode;
    /** Whether content is currently being streamed (shows caret indicator) */
    isStreaming?: boolean;
    /** Caret style for streaming indicator */
    caret?: MarkdownCaret;
    /** Additional CSS classes for the container */
    className?: string;
    /** Custom link renderer for internal app navigation */
    linkRenderer?: (props: { href?: string; children?: ReactNode }) => ReactNode;
    /** Show copy button for code blocks (default: true for static, false for streaming) */
    showCopyButton?: boolean;
}

/**
 * Markdown component powered by Streamdown.
 *
 * Supports both streaming (AI content) and static rendering modes.
 * Styling via `data-streamdown` CSS selectors in `styling/markdown.css`.
 *
 * The Streamdown renderer is loaded lazily. Until it is ready (or if its chunk fails
 * to load, or fails to render), the raw source is shown as escaped plain text in the same
 * container; a failed chunk load also offers a page reload, shown once per page. Routes that render markdown on load should use
 * `preloadMarkdownRenderer` as their loader.
 *
 * @example
 * <Markdown variant="chat" linkRenderer={ChatMarkdownLink}>
 *   {chatMessage}
 * </Markdown>
 */
export function Markdown(props: MarkdownProps) {
    const { children, variant = 'default', className = '' } = props;
    const containerClassName = `streamdown-${variant} ${className}`.trim();
    const fallback = <div className={cn(containerClassName, 'whitespace-pre-wrap')}>{children}</div>;

    // Once the chunk has loaded (for example via `preloadMarkdownRenderer`), render it directly
    // so the fallback is never committed, not even for the tick a resolved lazy would take.
    // Chosen once per mount so a later render never swaps the element type and remounts it.
    const [Renderer] = useState(() => loadedRenderer ?? createLazyMarkdownRenderer());

    return (
        <SourceFallbackContext.Provider value={fallback}>
            <Sentry.ErrorBoundary fallback={RendererErrorFallback}>
                <Suspense fallback={fallback}>
                    <Renderer {...props} />
                </Suspense>
            </Sentry.ErrorBoundary>
        </SourceFallbackContext.Provider>
    );
}

export default Markdown;
